<template>
  <div class="ws-slider">
    <div class="row items-center">
      <div
        v-if="cfg.label"
        class="col-3 text-left text-ws-ui-primary"
      >
        <small><strong>{{ cfg.label }}</strong></small>
      </div>
      <div class="col col-grow">
        <q-slider
          snap
          :value="value"
          v-bind="$attrs"
          :min="cfg.min"
          :max="cfg.max"
          :step="cfg.step"
          :color="$attrs.disable ? 'grey' : cfg.color"
          @input="emitValue"
          v-on="$listeners"
        />
        <div
          v-if="cfg.minLabel && cfg.maxLabel"
          class="row justify-between items-center"
        >
          <small>{{ cfg.minLabel }}</small>
          <small>{{ cfg.maxLabel }}</small>
        </div>
      </div>
      <div
        v-if="cfg.price !== undefined"
        class="col-auto"
      >
        <small class="addon-price text-right text-ws-ui-primary">
          {{
            $n(price, {
              style: 'currency',
              currency,
              currencyDisplay: 'symbol',
            })
          }}
        </small>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {Component, Prop} from 'vue-property-decorator';
import {mixins} from 'vue-class-component';
import {QSlider} from 'quasar';

import {debounce, type DebouncedFunc} from 'lodash-es';
import {config} from '@odin/services';
import {WsFieldMixin} from '@WS_Components/WsFieldMixin.vue';
import type {WsSliderOptions} from './WsSlider';

@Component({
  components: {
    QSlider,
  },
})
export class WsSlider extends mixins(WsFieldMixin) {
  @Prop({required: true, type: Number}) declare readonly value: number;
  @Prop({required: true}) readonly config!: WsSliderOptions;
  @Prop({default: 'top'}) readonly position!: string;

  get cfg(): WsSliderOptions {
    return {
      // defaults
      step: 1,
      max: null,
      // maxLabel: '∞',
      min: 0,
      // minLabel: '0',
      price: undefined,
      debounceTime: 200,
      // actual
      ...this.config,
    };
  }

  emitValue(value: number) {
    this.$emit('input', value);
  }

  created() {
    this.emitValue = debounce(this.emitValue, this.cfg.debounceTime) as DebouncedFunc<(val: number) => void>;
  }

  get currency() {
    return config.currency;
  }

  get price(): number {
    return this.value * this.cfg.price!;
  }
}

export default WsSlider;
</script>

<style lang="scss" scoped>
.ws-slider {
  .addon-price {
    min-width: 98px;
    padding: map-get($space-xs, y) 0 map-get($space-xs, y) map-get($space-lg, y);
  }

  .q-slider {
    &.q-slider--h {
      height: 28px;
    }

    &.position-bottom,
    &.position-bottom.q-slider--editable:hover {
      ::v-deep {
        .q-slider__pin--h {
          transform: translateY(26px);
        }

        .q-slider__arrow--h {
          transform: translateY(-2px) rotate(180deg);
        }

        .q-slider__thumb-container {
          margin-top: -8px;
        }

        .q-slider__pin-text {
          @include ws-typo-button-sm;
        }
      }
    }

    /* label always visible even during drag n drop */
    &.q-slider--active.q-slider--label ::v-deep .q-slider__thumb {
      transform: scale(1) !important;
    }
  }
}
</style>
